import { FC, type KeyboardEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PerformanceCardSkeleton } from './PerformanceCardSkeleton';
import { cva } from 'class-variance-authority';

import { cn } from '@/utils/tailwind';

import { CriticalWarningIcon, TrendDownIcon, TrendUpIcon, UsersIcon } from '../../../assets/icons';
import { Card, CardContent } from '../../atoms/Card/Card';
import { Slider } from '../../atoms/Slider/Slider';

type PerformanceCardProps = {
  title: string;
  score?: number | null;
  comparedToAverage?: number | null;
  isLoading?: boolean;
  scoreDisplay?: 'percentage' | 'enpsScore';
  reportCount?: number;
  onCardClick?: () => void;
};

const colorVariant = cva('', {
  variants: {
    variant: {
      success: '!text-custom-emerald-700',
      declined: '!text-custom-red-700',
      neutral: '!text-custom-warm-grey-800',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
});

const backgroundSliderVariant = cva('', {
  variants: {
    variant: {
      success: 'bg-custom-emerald-600',
      declined: 'bg-custom-red-600',
      neutral: 'bg-custom-warm-grey-700',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
});

const backgroundThumbVariant = cva('border-3', {
  variants: {
    variant: {
      success: 'border-custom-emerald-600',
      declined: 'border-custom-red-600',
      neutral: 'border-custom-warm-grey-700',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
});

export const PerformanceCard: FC<PerformanceCardProps> = ({
  title,
  comparedToAverage,
  score,
  isLoading,
  scoreDisplay = 'percentage',
  reportCount,
  onCardClick,
}) => {
  const { t } = useTranslation('components');

  const hasPerformanceData = score != null && comparedToAverage != null;
  const scoreValue = hasPerformanceData ? score : 0;
  const comparedToAverageValue = hasPerformanceData ? comparedToAverage : 0;
  const computedVariant =
    comparedToAverageValue > 0 ? 'success' : comparedToAverageValue < 0 ? 'declined' : 'neutral';

  const showCiriticalWarningIcon = computedVariant === 'declined';
  const valueUnit = scoreDisplay === 'enpsScore' ? '' : '%';

  const baseLineText = useMemo(() => {
    switch (computedVariant) {
      case 'success': {
        return t('teamPerformanceCard.comparedToAverage.more');
      }
      case 'declined': {
        return t('teamPerformanceCard.comparedToAverage.less');
      }
      case 'neutral': {
        return t('teamPerformanceCard.comparedToAverage.same');
      }
    }
  }, [computedVariant, t]);

  const trendIcon = useMemo(() => {
    switch (computedVariant) {
      case 'success': {
        return <TrendUpIcon width={19} height={19} />;
      }
      case 'declined': {
        return <TrendDownIcon width={19} height={19} />;
      }
      case 'neutral': {
        return (
          <span className="text-body-20-semibold text-custom-warm-grey-500" aria-hidden>
            —
          </span>
        );
      }
    }
  }, [computedVariant]);

  const renderEmptyState = () => {
    return (
      <Card className="border rounded-[12px] bg-white p-4 min-h-[158px]">
        <CardContent className="flex flex-col">
          <div>
            <p className="text-body-14-semibold !text-custom-warm-grey-700 mb-2">{title}</p>
            <p className="text-body-14-regular !text-custom-warm-grey-500">
              {t('teamPerformanceCard.notEnoughResponses')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return <PerformanceCardSkeleton />;
  }

  if (!hasPerformanceData) {
    return renderEmptyState();
  }

  const interactiveProps = onCardClick
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: onCardClick,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick();
          }
        },
      }
    : {};

  return (
    <Card
      className={cn(
        'border rounded-[12px] bg-white p-4 min-h-[158px]',
        onCardClick &&
          'cursor-pointer hover:bg-custom-warm-grey-25/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-custom-warm-grey-300'
      )}
      {...interactiveProps}
    >
      <CardContent className="flex flex-col">
        <div className="flex items-center justify-between gap-2 min-h-[20px] mb-2">
          <p className="text-body-14-semibold !text-custom-warm-grey-700">{title}</p>
          <div className="flex shrink-0 items-center gap-2">
            {reportCount !== undefined && (
              <span className="flex items-center gap-1 text-body-12-semibold text-custom-warm-grey-500">
                <UsersIcon width={16} height={16} />
                {reportCount}
              </span>
            )}
            {showCiriticalWarningIcon && (
              <span className="text-custom-red-500">
                <CriticalWarningIcon width={20} height={20} />
              </span>
            )}
          </div>
        </div>

        <div className="mb-9 flex items-center gap-2">
          <div>
            <div
              className={cn(
                'flex items-center gap-1.5 min-h-6',
                colorVariant({ variant: computedVariant })
              )}
            >
              <span className="text-body-20-semibold">
                {scoreValue}
                {valueUnit}
              </span>
              {trendIcon}
            </div>
            <div className="flex items-center gap-1.5 min-h-6">
              {computedVariant === 'neutral' ? (
                <span className="text-body-14-regular !text-custom-warm-grey-500">
                  {baseLineText}
                </span>
              ) : (
                <>
                  <span
                    className={cn(
                      'text-body-14-semibold',
                      colorVariant({ variant: computedVariant })
                    )}
                  >
                    {Math.abs(comparedToAverageValue)}
                    {valueUnit}
                  </span>
                  <span className="text-body-14-regular">{baseLineText}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="relative w-full">
          <Slider
            rangeClassName={backgroundSliderVariant({ variant: computedVariant })}
            thumbClassName={backgroundThumbVariant({ variant: computedVariant })}
            value={[scoreValue]}
            max={100}
            step={1}
            className="w-full"
            disabled
          />
        </div>
      </CardContent>
    </Card>
  );
};
